import { useMemo, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { AdminPrimaryButton, AdminSoftSelect } from "../admin/AdminPageShell";
import { CustomMonthPicker } from "../common/DatePickers";
import api from "../../utils/api";
import { HrAttendance } from "../../types/HrTypes";
import {
  LeaveBalanceReportRow,
  ReportEmployee,
  currentMonthValue,
  currentYear,
  downloadAttendanceWorkbook,
  downloadLeaveBalanceWorkbook,
  monthBounds,
  todayIso,
  yearBounds,
} from "../../utils/attendanceReportExcel";

function apiMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
    fallback
  );
}

const AttendanceReportDownloads = ({ employees }: { employees: ReportEmployee[] }) => {
  const [balanceYear, setBalanceYear] = useState(String(currentYear()));
  const [attendanceMode, setAttendanceMode] = useState<"month" | "year">("month");
  const [reportMonth, setReportMonth] = useState(currentMonthValue());
  const [reportYear, setReportYear] = useState(String(currentYear()));
  const [busy, setBusy] = useState<"balances" | "attendance" | null>(null);
  const [error, setError] = useState("");

  const years = useMemo(() => {
    const now = currentYear();
    return Array.from({ length: 5 }, (_, i) => String(now - i));
  }, []);

  const downloadBalances = async () => {
    setBusy("balances");
    setError("");
    try {
      const year = Number(balanceYear);
      const res = await api.get("/hr/reports/leave-balances", { params: { year } });
      const rows = (res.data?.data?.rows || []) as LeaveBalanceReportRow[];
      downloadLeaveBalanceWorkbook(year, rows);
    } catch (err) {
      setError(apiMessage(err, "Could not download leave balances"));
    } finally {
      setBusy(null);
    }
  };

  const downloadAttendance = async () => {
    setBusy("attendance");
    setError("");
    try {
      const today = todayIso();
      const range =
        attendanceMode === "month"
          ? monthBounds(reportMonth, today)
          : yearBounds(Number(reportYear), today);
      if (!range) {
        setError("Pick a month or year that has already started.");
        setBusy(null);
        return;
      }
      const res = await api.get("/allocation-tool/employee/attendance", {
        params: {
          from: range.from,
          to: range.to,
          period: "monthly",
          month: attendanceMode === "month" ? reportMonth : undefined,
        },
      });
      const rows = (res.data?.data?.attendance || []) as HrAttendance[];
      if (attendanceMode === "month") {
        const [year, month] = reportMonth.split("-").map(Number);
        downloadAttendanceWorkbook({
          mode: "month",
          year,
          month,
          employees,
          rows,
          today,
        });
      } else {
        downloadAttendanceWorkbook({
          mode: "year",
          year: Number(reportYear),
          employees,
          rows,
          today,
        });
      }
    } catch (err) {
      setError(apiMessage(err, "Could not download attendance"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4">
      <p className="text-sm font-semibold text-[#2C2C2A]">Download reports</p>
      <p className="mt-1 text-xs leading-5 text-[#8A8A85]">
        Leave balances list every employee’s PL, CL, and Holiday days still left, plus unpaid
        days already taken. Attendance lists each day as Present, Absent, Week off, or the leave
        taken. A full year is January, then February, then the rest of the year.
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[#E8E8E4] bg-[#FDFBF2] p-3">
          <p className="text-xs font-semibold text-[#4A4A48]">Leave balances</p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-xs text-[#8A8A85]">
              Year
              <AdminSoftSelect
                className="mt-1 block"
                value={balanceYear}
                onChange={(e) => setBalanceYear(e.target.value)}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </AdminSoftSelect>
            </label>
            <AdminPrimaryButton
              onClick={downloadBalances}
              disabled={busy !== null}
            >
              <FiDownload />
              {busy === "balances" ? "Preparing…" : "Download"}
            </AdminPrimaryButton>
          </div>
        </div>

        <div className="rounded-xl border border-[#E8E8E4] bg-[#FDFBF2] p-3">
          <p className="text-xs font-semibold text-[#4A4A48]">Attendance</p>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#4A4A48]">
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="attendance-report-scope"
                checked={attendanceMode === "month"}
                onChange={() => setAttendanceMode("month")}
              />
              One month
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="attendance-report-scope"
                checked={attendanceMode === "year"}
                onChange={() => setAttendanceMode("year")}
              />
              Full year
            </label>
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            {attendanceMode === "month" ? (
              <div className="min-w-[180px]">
                <CustomMonthPicker
                  label="Month"
                  value={reportMonth}
                  max={currentMonthValue()}
                  onChange={setReportMonth}
                />
              </div>
            ) : (
              <label className="text-xs text-[#8A8A85]">
                Year
                <AdminSoftSelect
                  className="mt-1 block"
                  value={reportYear}
                  onChange={(e) => setReportYear(e.target.value)}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </AdminSoftSelect>
              </label>
            )}
            <AdminPrimaryButton
              onClick={downloadAttendance}
              disabled={busy !== null}
            >
              <FiDownload />
              {busy === "attendance" ? "Preparing…" : "Download"}
            </AdminPrimaryButton>
          </div>
        </div>
      </div>

      {error ? <p className="mt-3 text-xs text-red-600">{error}</p> : null}
    </div>
  );
};

export default AttendanceReportDownloads;
